import React, { useState } from 'react';

//Components
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { PopoverActions } from '../shared';
import { MenuList, MenuItem } from '@mui/material';
import { Modal } from '../shared/modal';
import { GenerateRubricOptions } from './generate-rubric-options';
import { ConfirmationDialog } from '../shared';
import { useIsMobile } from '../../hooks/use-is-mobile';

//Hook & Constants & actions
import { useGetAllRubricsQuery, useRemoveAllJudgeFromRubricMutation } from '../../redux';
import { actionValidator } from './event-handler';
import { useEnqueueStacks } from '../../hooks/use-enqueue-stacks';

export const RubricTable = React.memo(function () {
  const isMobile = useIsMobile();
  const [modalProps, setModalProps] = useState({
    showModal: false,
    componentChildren: null,
  });
  const [confirmationDialog, setconfirmationDialog] = useState({ show: false, rubricId: '' });
  const [removeAllJudgesFromRubric] = useRemoveAllJudgeFromRubricMutation();
  const { data: rubricList } = useGetAllRubricsQuery();
  const { enqueueSuccess, enqueueError } = useEnqueueStacks();

  const handleOpen = component => {
    setModalProps(curr => {
      return { ...curr, showModal: true, componentChildren: component };
    });
  };
  const handleClose = () => {
    setModalProps(curr => {
      return { ...curr, showModal: false, componentChildren: null };
    });
  };

  const handleClick = event => {
    const res = actionValidator(event.target.id, null, handleClose);
    handleOpen(res);
  };

  const handleOpenDialog = rubricId => {
    setconfirmationDialog(curr => {
      return { ...curr, show: true, rubricId: rubricId };
    });
  };

  const handleCloseDialog = () => {
    setconfirmationDialog(curr => {
      return { ...curr, show: false, rubricId: '' };
    });
  };

  const handleSubmit = async () => {
    const res = await removeAllJudgesFromRubric({ rubricId: confirmationDialog.rubricId });
    if (!res.error) {
      enqueueSuccess(`La Rubrica fue actualizada de forma exitosa`, {
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    } else {
      enqueueError(`Ha ocurrido un error, intente mas tarde o comunicarse con el administrador`, {
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    }
    handleCloseDialog();
  };

  if (!rubricList) {
    return <>Loading...</>;
  }

  return (
    <>
      <GenerateRubricOptions handleClick={handleClick} />

      {/* Mobile: card list */}
      {isMobile && (
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          {rubricList.map(rubric => (
            <Card key={rubric._id} variant='outlined'>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Typography variant='subtitle2' fontWeight={700} sx={{ mb: 0.5 }}>{rubric.name}</Typography>
                <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 1 }}>
                  {rubric.event?.name || '—'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label={`${rubric.maxPoints} pts`} size='small' variant='outlined' />
                  <Chip label={`${rubric.assignedJudge?.length || 0} jueces`} size='small' variant='outlined' />
                  <Box sx={{ flex: 1 }} />
                  <PopoverActions>
                    {dotActionProps => {
                      const { onClick: dotActionOnClick } = dotActionProps;
                      const handleMenuClick = event => {
                        event.preventDefault();
                        const res = actionValidator(event.target.id, event.target.dataset['rubric'], handleClose);
                        handleOpen(res);
                        dotActionOnClick();
                      };
                      return (
                        <MenuList>
                          <MenuItem id='RemoveJudgesRubric' onClick={() => { handleOpenDialog(rubric._id); dotActionOnClick(); }}>
                            Eliminar asignaciones de Juez
                          </MenuItem>
                          <MenuItem id='RemoveJudgeRubric' data-rubric={JSON.stringify(rubric)} onClick={handleMenuClick}>
                            Eliminar asignacion de Juez
                          </MenuItem>
                        </MenuList>
                      );
                    }}
                  </PopoverActions>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Desktop: table */}
      {!isMobile && (
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label='simple table'>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Nombre de Evento</TableCell>
              <TableCell>Nombre de Rubrica</TableCell>
              <TableCell align='right'>Puntos Maximo</TableCell>
              <TableCell align='right'>Cantidad de Jueces</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rubricList.map(rubric => (
              <TableRow key={rubric._id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell>{rubric._id}</TableCell>
                <TableCell component='th' scope='row'>
                  {rubric.event.name}
                </TableCell>
                <TableCell component='th' scope='row'>
                  {rubric.name}
                </TableCell>
                <TableCell align='center'>{rubric.maxPoints}</TableCell>
                <TableCell align='center'>{rubric.assignedJudge.length}</TableCell>
                <TableCell>
                  <PopoverActions>
                    {dotActionProps => {
                      const { onClick: dotActionOnClick } = dotActionProps;

                      const handleClick = event => {
                        event.preventDefault();

                        const res = actionValidator(event.target.id, event.target.dataset['rubric'], handleClose);

                        handleOpen(res);

                        dotActionOnClick();
                      };

                      return (
                        <MenuList>
                          <MenuItem id={'RemoveJudgesRubric'} onClick={() => handleOpenDialog(rubric._id)}>
                            Eliminar asignaciones de Juez
                          </MenuItem>
                          <MenuItem id={'RemoveJudgeRubric'} data-rubric={JSON.stringify(rubric)} onClick={handleClick}>
                            Eliminar asignacion de Juez
                          </MenuItem>
                        </MenuList>
                      );
                    }}
                  </PopoverActions>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      )}
      <ConfirmationDialog
        open={confirmationDialog.show}
        handleClose={handleCloseDialog}
        handleSubmit={handleSubmit}
        title='Eliminar asignaciones'
        bodyText={'Esta seguro que desea eliminar todas las asignaciones del jueces a este rubro?'}
      />
      <Modal
        componentChildren={modalProps.componentChildren}
        showModal={modalProps.showModal}
        handleClose={handleClose}
      />
    </>
  );
});
